import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Col, Row, Typography } from 'antd';
import { Button, EmptyState, PageHeader, Spinner } from '@shared/ui';
import { ROUTES } from '@shared/config/routes';
import { useCurrentUserQuery } from '@entities/session';
import {
  loadStoredRange,
  persistRange,
  useDashboardQuery,
  type AnalyticsRange,
} from '@entities/analytics';
import { RangeSwitcher } from './range-switcher';
import { KpiCards } from './kpi-cards';
import { Funnel } from './funnel';
import { DailyChart } from './daily-chart';

const { Title, Paragraph } = Typography;

function isEmpty(data: ReturnType<typeof useDashboardQuery>['data']): boolean {
  if (!data) return true;
  const t = data.totals;
  return (
    t.PROFILE_VIEW === 0 &&
    t.PRODUCT_VIEW === 0 &&
    t.REQUEST_RECEIVED === 0 &&
    t.CHAT_STARTED === 0 &&
    t.DEAL_WON === 0
  );
}

export function AnalyticsDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: user } = useCurrentUserQuery();
  const [range, setRange] = useState<AnalyticsRange>(() => loadStoredRange());
  const { data, isLoading, isError, error, refetch } = useDashboardQuery(range);

  const onRangeChange = (next: AnalyticsRange) => {
    setRange(next);
    persistRange(next);
  };

  const greetingName = user?.email?.split('@')[0] ?? 'there';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <section
        className="brand-gradient"
        style={{
          borderRadius: 16,
          padding: 28,
          border: '1px solid var(--ant-color-border-secondary)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 24,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <Title level={3} style={{ margin: 0, fontWeight: 600 }}>
              {t('dashboard.greeting', { name: greetingName })}
            </Title>
            <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0 }}>
              {t('dashboard.subtitle')}
            </Paragraph>
          </div>
          <RangeSwitcher value={range} onChange={onRangeChange} />
        </div>
      </section>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner />
        </div>
      ) : isError ? (
        <Alert
          type="error"
          showIcon
          message={t('dashboard.loadFail', { message: error?.message ?? 'unknown' })}
          action={
            <Button size="sm" variant="outline" onClick={() => void refetch()}>
              {t('common.retry')}
            </Button>
          }
        />
      ) : isEmpty(data) ? (
        <EmptyState
          title={t('dashboard.empty.title')}
          description={t('dashboard.empty.description')}
          action={<Button onClick={() => navigate(ROUTES.products)}>{t('dashboard.empty.action')}</Button>}
        />
      ) : data ? (
        <>
          <KpiCards totals={data.totals} daily={data.daily} />
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={10}>
              <Funnel data={data.funnel} />
            </Col>
            <Col xs={24} lg={14}>
              <DailyChart data={data.daily} />
            </Col>
          </Row>
        </>
      ) : null}

      <PageHeader title={t('dashboard.title')} subtitle={t('dashboard.subtitle')} />
    </div>
  );
}
